import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";
import { Col, Row, Button } from 'reactstrap';
import { DataGrid } from 'components/dataGrid/dataGrid';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleCheck, faCircleXmark } from "@fortawesome/free-regular-svg-icons";
import { faCircleExclamation, faTriangleExclamation } from "@fortawesome/free-solid-svg-icons";
import PatientCoverageInsuredDetails from './patientCoverageInsuredDetails';
import PatientCoveragePlanDetails from './patientCoveragePlanDetails';
import { getPatientCoverage } from "../../../actions/patientAccessCuratorActions";
import LoadingIndicator from "@/components/loadingIndicator";
import '../css/patientCoverage.css';
import { useEffect, useState } from "react";

export default function PatientCoverage(props: any) {
    const gridKey = `grid-key-${Math.random()}`;
    const DATE_FORMAT = 'MM/DD/YYYY';
    const TIME_FORMAT = 'hh:mm A';

    const dispatch = useDispatch();
    const activePatient = useSelector((state: any) => state.activePatient);
    const config = useSelector((state: any) => state.config.patient);

    const patientCoverageFetchedAt = activePatient.patientCoverageFetchedAt;
    const refreshDuration = config.patientAccessCuratorRefreshTimeoutInHours;

    const [isLoading, setIsLoading] = useState(false);

    const handleRefresh = () => {
        const criteria = {
            patient: activePatient.details,
            dataValidityInDays: config.patientAccessCuratorDataValidityInDays,
            enableAutoFetch: config.enablePatientAccessCuratorCoverageAutoFetch,
            forceRefresh: true
        };
        setIsLoading(true);
        dispatch(async (dispatch: any) => {
            await dispatch(getPatientCoverage(criteria));
            setIsLoading(false);
        });
    }

    const columnDefs = [
        {
            field: 'payerName',
            headerName: 'Insurer',
            cellRenderer: 'agGroupCellRenderer',
        },
        {
            field: 'confidenceLevel',
            headerName: 'Confidence',
            cellClass: (r: any) => {
                return (r.data.confidenceLevel === 'Confident') ? 'coverageConfident' : 'coverageNotConfident';
            }
        },
        {
            field: 'hierarchy',
            headerName: 'Primacy',
            cellClass: (r: any) => {
                return (r.data.hierarchy === 'Primary') ? 'hierarchyPrimary' : (r.data.hierarchy === 'Secondary') ? 'hierarchySecondary' : (r.data.hierarchy === 'Tertiary') ? 'hierarchyTertiary' : '';
            }
        },
        {
            field: 'status',
            headerName: 'Status',
            cellRenderer: (r: any) => {
                if (r.data.status === 'Active') {
                    return (
                        <>
                            <span style={{ marginRight: "10px" }} className="text-success"><FontAwesomeIcon icon={faCircleCheck} /></span>
                            <span>{r.data.status}</span>
                        </>
                    )
                }
                else if (r.data.status === 'Inactive') {
                    return (
                        <>
                            <span style={{ marginRight: "10px" }} className="text-warning"><FontAwesomeIcon icon={faCircleExclamation} /></span>
                            <span>{r.data.status}</span>
                        </>
                    )
                }
                else if (r.data.status === 'Rejected') {
                    return (
                        <>
                            <span style={{ marginRight: "10px" }} className="text-danger"><FontAwesomeIcon icon={faCircleXmark} /></span>
                            <span>{r.data.status}</span>
                        </>
                    )
                }
                else if (r.data.status === 'Contact Payer') {
                    return (
                        <>
                            <span style={{ marginRight: "10px" }} className="text-secondary"><FontAwesomeIcon icon={faTriangleExclamation} /></span>
                            <span>{r.data.status}</span>
                        </>
                    )
                }
                else {
                    <span>{r.data.status}</span>
                }
            }
        },
        {
            field: 'memberId',
            headerName: 'Member ID',
        },
        {
            field: 'groupNumber',
            headerName: 'Group #',
        }
    ];

    const detailCellRendererParams = {
        detailGridOptions: {
            pagination: false,
            suppressContextMenu: true,
            headerHeight: 0,
            suppressRowClickSelection: true,
            getRowClass: (r: any) => 'patientCoverageDetailsRow',
            columnDefs: [
                {
                    field: 'patient',
                    autoHeight: true,
                    cellRenderer: (r: any) => {
                        return (
                            <PatientCoverageInsuredDetails patient={r.data.patient} />
                        )
                    }
                },
                {
                    field: 'plan',
                    autoHeight: true,
                    cellRenderer: (r: any) => {
                        return (
                            <PatientCoveragePlanDetails plan={r.data.plan} />
                        )
                    }
                },
            ],
            defaultColDef: {
                flex: 1,
            },
        },
        getDetailRowData: (params: any) => {
            params.successCallback(params.data.coverageDataInformation);
        },
    }

    return (
        <div>
            {isLoading ? <LoadingIndicator /> :
                <>
                    <Row style={{ marginTop: '10px' }}>
                        <Col>
                            <p>As of {patientCoverageFetchedAt ? dayjs(patientCoverageFetchedAt).format(DATE_FORMAT) + ' at ' + dayjs(patientCoverageFetchedAt).format(TIME_FORMAT) : `${dayjs().format(DATE_FORMAT)} at ${dayjs().format(TIME_FORMAT)}`}</p>
                            {showRefreshButton(patientCoverageFetchedAt, refreshDuration) && <Button color="primary" onClick={handleRefresh} style={{ marginBottom: '10px' }}>Refresh</Button>}
                        </Col>
                    </Row>
                    <DataGrid
                        columnDefs={columnDefs}
                        data={activePatient.patientCoverage}
                        getRowClass={(r: any) => ''}
                        suppressRowClickSelection={true}
                        noRowsMessage='No coverage found based on demographics provided.'
                        childrenColumnDefs={detailCellRendererParams}
                        customGridSearch={undefined}
                        gridHeading={undefined}
                        gridKey={gridKey}
                        getRowHeight={undefined}
                        defaultPageSize={undefined}
                        editable={undefined}
                        enableColumnFilters={undefined}
                        setSelectedRows={undefined}
                        serverSideDatasource={undefined}
                    />
                </>
            }
        </div>
    )
}

const showRefreshButton = (lastModifiedDate: Date | null, refreshDuration: number) => {
    if(!lastModifiedDate)
        return true;
    
    const utcOffset = dayjs().utcOffset() / 60;
    const currentUtcDate = dayjs().add(utcOffset, "hour");

    return currentUtcDate > dayjs(lastModifiedDate).add(refreshDuration, "hour");
}